import { useState, useEffect, useRef } from "react";
import { startRecording, saveRecording } from "./handler";
import { Recorder, Interval, AudioTrack, MediaRecorderEvent } from "./recorder";

const initialState: Recorder = {
  recordingMinutes: 0,
  recordingSeconds: 0,
  initRecording: false,
  mediaStream: null,
  mediaRecorder: null,
  audio: null,
};

export default function useRecorder(onBlob) {
  const [recorderState, setRecorderState] = useState<Recorder>(initialState);
  // Use a ref to always have access to the current mediaRecorder
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);

  useEffect(() => {
    const MAX_RECORDER_TIME = 5;
    let recordingInterval: Interval = null;

    if (recorderState.initRecording)
      recordingInterval = setInterval(() => {
        setRecorderState((prevState: Recorder) => {
          if (
            prevState.recordingMinutes === MAX_RECORDER_TIME &&
            prevState.recordingSeconds === 0
          ) {
            typeof recordingInterval === "number" &&
              clearInterval(recordingInterval);
            return prevState;
          }

          if (
            prevState.recordingSeconds >= 0 &&
            prevState.recordingSeconds < 59
          )
            return {
              ...prevState,
              recordingSeconds: prevState.recordingSeconds + 1,
            };
          else if (prevState.recordingSeconds === 59)
            return {
              ...prevState,
              recordingMinutes: prevState.recordingMinutes + 1,
              recordingSeconds: 0,
            };
          else return prevState;
        });
      }, 1000);
    else
      typeof recordingInterval === "number" && clearInterval(recordingInterval);

    return () => {
      typeof recordingInterval === "number" && clearInterval(recordingInterval);
    };
  });

  useEffect(() => {
    console.log('🔄 [useRecorder] mediaStream effect triggered');
    console.log('🔄 [useRecorder] Has mediaStream:', !!recorderState.mediaStream);

    setRecorderState((prevState) => {
      if (prevState.mediaStream) {
        console.log('✅ [useRecorder] Creating new MediaRecorder');
        console.log('🔄 [useRecorder] Stream tracks count:', prevState.mediaStream.getTracks().length);

        const newMediaRecorder = new MediaRecorder(prevState.mediaStream);
        console.log('🎙️ [useRecorder] MediaRecorder created, state:', newMediaRecorder.state);
        console.log('🎙️ [useRecorder] MediaRecorder mimeType:', newMediaRecorder.mimeType);

        mediaRecorderRef.current = newMediaRecorder;
        return {
          ...prevState,
          mediaRecorder: newMediaRecorder,
        };
      } else {
        console.log('⚠️ [useRecorder] No mediaStream available, skipping MediaRecorder creation');
        return prevState;
      }
    });
  }, [recorderState.mediaStream]);

  // Keep the ref in sync with state changes
  useEffect(() => {
    console.log('🔄 [useRecorder] Syncing mediaRecorder ref, exists:', !!recorderState.mediaRecorder);
    mediaRecorderRef.current = recorderState.mediaRecorder;
  }, [recorderState.mediaRecorder]);

  useEffect(() => {
    console.log('🎬 [useRecorder] Main recorder effect triggered');
    const recorder = recorderState.mediaRecorder;
    console.log('🎬 [useRecorder] Recorder exists:', !!recorder);
    console.log('🎬 [useRecorder] Recorder state:', recorder?.state);

    let chunks: Blob[] = [];

    const blobToBase64 = (blob) => {
      console.log('📦 [useRecorder] Converting blob to base64, size:', blob.size, 'type:', blob.type);
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      return new Promise((resolve) => {
        reader.onloadend = () => {
          console.log('✅ [useRecorder] Base64 conversion complete, length:', reader.result?.toString().length);
          resolve(reader.result);
        };
      });
    };

    if (recorder && recorder.state === "inactive") {
      console.log('▶️ [useRecorder] Starting recorder...');
      recorder.start();
      console.log('▶️ [useRecorder] Recorder.start() called, new state:', recorder.state);

      recorder.ondataavailable = (e: MediaRecorderEvent) => {
        console.log('📥 [useRecorder] Data available event, data size:', e.data.size);
        chunks.push(e.data);
        console.log('📥 [useRecorder] Total chunks:', chunks.length);
      };

      recorder.onstop = async () => {
        console.log('🛑 [useRecorder] MediaRecorder onstop fired');
        console.log('🛑 [useRecorder] Total chunks collected:', chunks.length);
        console.log('🛑 [useRecorder] Chunk sizes:', chunks.map(c => c.size));

        const blob = new Blob(chunks, { type: "audio/webm; codecs=opus" });
        console.log('📦 [useRecorder] Blob created, size:', blob.size, 'type:', blob.type);
        chunks = [];

        console.log('📦 [useRecorder] Converting blob to base64...');
        const base64Data = await blobToBase64(blob);
        console.log('✅ [useRecorder] Base64 data ready, calling onBlob callback');
        console.log('📝 [useRecorder] Base64 prefix:', base64Data.toString().substring(0, 50));

        onBlob(base64Data);
        console.log('✅ [useRecorder] onBlob callback completed');

        // Stop all media stream tracks to properly release the microphone
        // This is critical for iOS to allow subsequent recordings
        if (recorder.stream) {
          console.log('🎙️ [useRecorder] Stopping audio tracks from recorder.stream');
          recorder.stream.getAudioTracks().forEach((track: AudioTrack) => {
            console.log('🎙️ [useRecorder] Stopping track:', track.label, 'state:', track.readyState);
            track.stop();
          });
        } else {
          console.log('⚠️ [useRecorder] No recorder.stream available');
        }

        setRecorderState((prevState: Recorder) => {
          console.log('🔄 [useRecorder] Resetting recorder state');
          // Also stop tracks from state's mediaStream as a fallback
          if (prevState.mediaStream) {
            console.log('🎙️ [useRecorder] Stopping tracks from state.mediaStream');
            prevState.mediaStream.getTracks().forEach(track => {
              console.log('🎙️ [useRecorder] Stopping fallback track:', track.label);
              track.stop();
            });
          }
          if (prevState.mediaRecorder) {
            console.log('✅ [useRecorder] Resetting to initial state with audio URL');
            return {
              ...initialState,
              audio: window.URL.createObjectURL(blob),
            };
          } else {
            console.log('✅ [useRecorder] Resetting to initial state (no mediaRecorder)');
            return initialState;
          }
        });
      };
    } else {
      if (!recorder) {
        console.log('⚠️ [useRecorder] No recorder available, skipping start');
      } else {
        console.log('⚠️ [useRecorder] Recorder state is not inactive:', recorder.state);
      }
    }

    // return () => {
    //   if (recorder)
    //     recorder.stream
    //       .getAudioTracks()
    //       .forEach((track: AudioTrack) => track.stop());
    // };
  }, [recorderState.mediaRecorder]);

  return {
    recorderState,
    startRecording: () => startRecording(setRecorderState),
    cancelRecording: () => {
      // Stop the media recorder if it's recording
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        // Stop the recorder without triggering onstop callback
        // We do this by removing the event handlers first
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.stop();
      }

      // Stop all media stream tracks
      if (recorderState.mediaStream) {
        recorderState.mediaStream.getTracks().forEach(track => track.stop());
      }

      // Reset state
      setRecorderState(initialState);
    },
    saveRecording: () => saveRecording(mediaRecorderRef.current),
  };
}
